import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Utility type to skip the first element of a tuple (used to extract args after 'old')
 */
type SkipFirst<T extends unknown[]> = T extends [unknown, ...infer Rest]
  ? Rest
  : [];

/**
 * Extract the arguments of an updater function, skipping the first 'old' parameter
 */
type ExtractUpdaterArgs<T> = T extends (...args: infer Args) => unknown
  ? SkipFirst<Args>
  : never;

/**
 * Creates an optimistic update helper for any query type.
 *
 * This helper provides methods for snapshot, update, rollback, and invalidate operations,
 * along with custom updaters that you define inline.
 *
 * @param queryClient - The React Query client instance
 * @param queryKey - The query key to operate on
 * @param updaters - Object of updater functions. Each receives (old: TData, ...args) and returns TData
 * @returns An object with optimistic update methods and typed custom updaters
 *
 * @example
 * ```typescript
 * const saleUpdaters = {
 *   updateDateTime: (old: CustomerSaleResponse, appointmentId: string, dateTime: string) => ({
 *     ...old,
 *     items: old.items.map((item) =>
 *       item.appointment?.id === appointmentId
 *         ? { ...item, appointment: { ...item.appointment, dateTime } }
 *         : item
 *     ),
 *   }),
 * };
 *
 * const sale = optimisticUpdateHelper<CustomerSaleResponse>()(
 *   queryClient,
 *   queryKey,
 *   saleUpdaters,
 * );
 * const previousData = await sale.snapshot();
 * sale.updateDateTime('apt-123', '2025-04-04T16:00:00.000Z');
 *
 * // On error:
 * sale.rollback(previousData);
 *
 * // On settled:
 * sale.invalidate();
 * ```
 */
export const optimisticUpdateHelper = <TData>() => {
  return <
    TUpdaters extends Record<string, (old: TData, ...args: never[]) => TData>,
  >(
    queryClient: QueryClient,
    queryKey: QueryKey,
    updaters: TUpdaters,
  ) => {
    // Transform updaters to auto-call setQueryData with null safety
    const customUpdaters = Object.keys(updaters).reduce(
      (acc, key) => {
        acc[key] = (...args: unknown[]) => {
          queryClient.setQueryData<TData>(queryKey, (old) => {
            if (!old) return old; // Skip if no data cached
            return (updaters[key] as (old: TData, ...args: unknown[]) => TData)(
              old,
              ...args,
            );
          });
        };
        return acc;
      },
      {} as Record<string, (...args: unknown[]) => void>,
    );

    return {
      /**
       * Cancel any outgoing refetches and snapshot current query data for rollback.
       * @returns The previous data, or undefined if not cached
       */
      async snapshot(): Promise<TData | undefined> {
        await queryClient.cancelQueries({ queryKey });
        return queryClient.getQueryData<TData>(queryKey);
      },

      /**
       * Rollback query to its previous state from a snapshot.
       * @param previousData - The snapshot data to restore
       */
      rollback(previousData: TData | undefined): void {
        if (previousData) {
          queryClient.setQueryData(queryKey, previousData);
        }
      },

      /**
       * Invalidate the query to trigger a refetch.
       */
      invalidate(): void {
        queryClient.invalidateQueries({ queryKey });
      },

      /**
       * Apply a custom updater function directly.
       * @param updater - Function that transforms old data into new data
       */
      updateWith(updater: (old: TData) => TData): void {
        queryClient.setQueryData<TData>(queryKey, (old) =>
          old ? updater(old) : old,
        );
      },

      ...customUpdaters,
    } as {
      snapshot: () => Promise<TData | undefined>;
      rollback: (previousData: TData | undefined) => void;
      invalidate: () => void;
      updateWith: (updater: (old: TData) => TData) => void;
    } & {
      [K in keyof TUpdaters]: (
        ...args: ExtractUpdaterArgs<TUpdaters[K]>
      ) => void;
    };
  };
};
